import React, { useState } from "react";
import { Restaurant } from "../../../services/types";
import Modal from "./Modal";
import ChargeRateModal from "./ChargeRateModal";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import AdminService from "src/services/admin.services";
import CreateSuspension from "src/pages/settings/components/CreateSuspension";
import ConfirmationModal from "src/components/ui/ConfirmationModal";
import {
  FiMapPin,
  FiClock,
  FiMail,
  FiPhone,
  FiCheckCircle,
  FiToggleLeft,
  FiToggleRight,
  FiX,
  FiDollarSign,
} from "react-icons/fi";
import { FaCopy } from "react-icons/fa";

const adminService = new AdminService();

const mapContainerStyle = {
  height: "400px",
  width: "100%",
};

interface RestaurantDetailsProps {
  restaurant: Restaurant;
  onStatusChange: (newStatus: boolean) => Promise<void>;
  checkRestaurant: (agent: { email: string }) => Promise<any>;
}

const RestaurantDetails: React.FC<RestaurantDetailsProps> = ({
  restaurant,
  onStatusChange,
  checkRestaurant,
}) => {
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerified, setIsVerified] = useState(restaurant.is_activated);
  const [copied, setCopied] = useState(false);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [showSuspendConfirmModal, setShowSuspendConfirmModal] = useState(false);
  const [showVerifyConfirmModal, setShowVerifyConfirmModal] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showChargeRateModal, setShowChargeRateModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [isCrediting, setIsCrediting] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(restaurant._id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleActivate = async () => {
    try {
      await onStatusChange(true);
      setIsActivateModalOpen(false);
      setSuccessMessage("Restaurant activated successfully");
      setIsSuccessModalOpen(true);
    } catch (error) {
      setErrorMessage("Failed to activate restaurant");
      setIsErrorModalOpen(true);
    }
  };

  const handleVerify = () => {
    if (isVerified) {
      setSuccessMessage("Restaurant document has already been checked.");
      setIsSuccessModalOpen(true);
      return;
    }
    setShowVerifyConfirmModal(true);
  };

  const confirmVerify = async () => {
    try {
      setIsVerifying(true);
      setShowVerifyConfirmModal(false);

      const response = await checkRestaurant({ email: restaurant.email });

      if (response?.success) {
        setSuccessMessage(response.message || "Verification successful!");
        setIsSuccessModalOpen(true);
        setIsVerified(true);
      } else if (response?.message?.includes("already approved")) {
        setErrorMessage("This restaurant is already verified.");
        setIsErrorModalOpen(true);
      } else {
        throw new Error(response?.message || "Verification failed.");
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "An unknown error occurred.";
      console.error("Detailed error:", error);
      setErrorMessage(errorMsg);
      setIsErrorModalOpen(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSuspension = async () => {
    if (restaurant.is_suspended) {
      try {
        const response = await adminService.updateSuspensionStatus(
          restaurant._id
        );

        if (response?.success) {
          setSuccessMessage("Suspension status updated successfully.");
          setIsSuccessModalOpen(true);
        } else {
          setErrorMessage("Failed to update suspension status.");
          setIsErrorModalOpen(true);
        }
      } catch (error) {
        setErrorMessage("An error occurred while updating suspension status.");
        setIsErrorModalOpen(true);
      }
    } else {
      setShowSuspendConfirmModal(true);
    }
  };

  const confirmSuspendRestaurant = () => {
    setIsSuspending(true);
    setShowSuspendConfirmModal(false);
    setShowSuspensionModal(true);
    setIsSuspending(false);
  };

  const handleUpdateChargeRate = async (rate: number) => {
    try {
      await adminService.updateRestaurantChargeRate(restaurant._id, rate);
      setSuccessMessage(`Commission updated to ${rate}% successfully.`);
      setIsSuccessModalOpen(true);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Failed to update commission.";
      setErrorMessage(errorMsg);
      setIsErrorModalOpen(true);
    }
  };

  const handleCreditRestaurant = async () => {
    const amountValue = Number(creditAmount);
    if (!creditAmount || Number.isNaN(amountValue) || amountValue <= 0) {
      setCreditError("Please enter a valid amount greater than zero.");
      return;
    }

    try {
      setIsCrediting(true);
      setCreditError(null);
      await adminService.creditRestaurantWallet(restaurant._id, amountValue);
      setSuccessMessage("Restaurant wallet credited successfully.");
      setIsSuccessModalOpen(true);
      setShowCreditModal(false);
      setCreditAmount("");
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Failed to credit restaurant.";
      setErrorMessage(errorMsg);
      setIsErrorModalOpen(true);
    } finally {
      setIsCrediting(false);
    }
  };

  const closeCreditModal = () => {
    setShowCreditModal(false);
    setCreditAmount("");
    setCreditError(null);
  };

  const getStatusColor = () => {
    if (restaurant.is_suspended) return "bg-yellow-100 text-yellow-800";
    if (!isVerified) return "bg-gray-100 text-gray-800";
    return "bg-green-100 text-green-800";
  };

  const center =
    restaurant.location?.Coordinates?.length >= 2
      ? {
          lat: restaurant.location.Coordinates[1],
          lng: restaurant.location.Coordinates[0],
        }
      : { lat: 0, lng: 0 };

  return (
    <div className="w-full bg-white rounded-xl shadow-md overflow-hidden">
      {/* Restaurant ID Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <span>Restaurant ID:</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1.5 bg-white rounded-md text-gray-700 text-sm truncate max-w-xs">
              {restaurant._id}
            </div>
            <button
              onClick={handleCopy}
              className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
              aria-label="Copy Restaurant ID"
            >
              <FaCopy className="w-4 h-4" />
            </button>
          </div>
        </div>
        {copied && (
          <div className="text-xs text-green-500 mt-1 text-right animate-fade-in">
            Copied to clipboard!
          </div>
        )}
      </div>

      {/* Restaurant Header */}
      <div className="relative h-48 bg-gradient-to-r from-blue-500 to-purple-600">
        <img
          src={restaurant.image_url || "/default-restaurant.jpg"}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <div className="text-center p-6 text-white">
            <h1 className="text-2xl font-bold">{restaurant.name}</h1>
            <div
              className={`inline-flex items-center px-3 py-1 mt-2 rounded-full text-xs font-medium ${getStatusColor()}`}
            >
              {restaurant.is_suspended
                ? "Suspended"
                : isVerified
                ? "Active"
                : "Inactive"}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 md:p-8">
        {/* Profile Section */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
          <div className="relative -mt-20">
            <img
              src={restaurant.image_url || "/default-restaurant.jpg"}
              alt={restaurant.name}
              className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
            />
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-800">
              {restaurant.name}
            </h2>
            <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-4">
              <div className="flex items-center text-gray-600">
                <FiMail className="mr-2" />
                <span>{restaurant.email}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <FiPhone className="mr-2" />
                <span>{restaurant.phone || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <DetailCard
            icon={<FiMapPin className="w-5 h-5" />}
            title="Address"
            value={restaurant.address || "N/A"}
          />
          <DetailCard
            icon={<FiClock className="w-5 h-5" />}
            title="Operating Hours"
            value={
              restaurant.days_open?.map((day) => (
                <div key={day.day} className="mb-1">
                  <span className="font-medium">{day.day}:</span> {day.open} -{" "}
                  {day.close}
                </div>
              )) || "N/A"
            }
          />
        </div>

        {/* Location Map */}
        {restaurant.location?.Coordinates?.length >= 2 ? (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">
              Location
            </h3>
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <LoadScript googleMapsApiKey="AIzaSyD845dpQ62RHqNW83JcyA5YKaRQ05UVl8I">
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={center}
                  zoom={15}
                >
                  <Marker position={center} />
                </GoogleMap>
              </LoadScript>
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Coordinates: {restaurant.location.Coordinates[1].toFixed(6)},{" "}
              {restaurant.location.Coordinates[0].toFixed(6)}
            </div>
          </div>
        ) : (
          <div className="mb-8 p-4 bg-red-50 text-red-600 rounded-lg">
            Location coordinates not available
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleVerify}
            disabled={restaurant.checked_by_admin}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              restaurant.checked_by_admin
                ? "bg-green-100 text-green-700 cursor-not-allowed"
                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
            }`}
          >
            {restaurant.checked_by_admin ? (
              <>
                <FiCheckCircle className="w-5 h-5" />
                Verified
              </>
            ) : (
              <>
                <FiCheckCircle className="w-5 h-5" />
                Verify Restaurant
              </>
            )}
          </button>

          <button
            onClick={() => setIsActivateModalOpen(true)}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              restaurant.is_activated
                ? "bg-red-100 text-red-700 hover:bg-red-200"
                : "bg-green-100 text-green-700 hover:bg-green-200"
            }`}
          >
            {restaurant.is_activated ? (
              <>
                <FiToggleLeft className="w-5 h-5" />
                Deactivate
              </>
            ) : (
              <>
                <FiToggleRight className="w-5 h-5" />
                Activate
              </>
            )}
          </button>

          <button
            onClick={handleSuspension}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              restaurant.is_suspended
                ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            {restaurant.is_suspended
              ? "Remove Suspension"
              : "Suspend Restaurant"}
          </button>

          <button
            onClick={() => setShowChargeRateModal(true)}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
          >
            Update Commission
          </button>

          <button
            onClick={() => setShowCreditModal(true)}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
          >
            <FiDollarSign className="w-5 h-5" />
            Credit Restaurant
          </button>
        </div>
      </div>

      {/* Modals */}
      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showVerifyConfirmModal}
        onClose={() => setShowVerifyConfirmModal(false)}
        onConfirm={confirmVerify}
        title="Verify Restaurant"
        message={`Are you sure you want to verify ${restaurant.name}? This will approve their documents and allow them to operate on the platform.`}
        confirmText="Verify Restaurant"
        cancelText="Cancel"
        type="warning"
        entityName={restaurant.name}
        entityType="restaurant"
        isLoading={isVerifying}
        loadingText="Verifying..."
      />

      <ConfirmationModal
        isOpen={showSuspendConfirmModal}
        onClose={() => setShowSuspendConfirmModal(false)}
        onConfirm={confirmSuspendRestaurant}
        title="Suspend Restaurant"
        message="Are you sure you want to suspend this restaurant? They will be temporarily blocked from accessing the platform."
        confirmText="Suspend"
        cancelText="Cancel"
        type="warning"
        entityName={restaurant.name}
        entityType="restaurant"
        isLoading={isSuspending}
        loadingText="Processing..."
      />

      <Modal
        isOpen={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        title="Activate Restaurant"
        message="Are you sure you want to activate this restaurant?"
        confirmText="Yes"
        cancelText="Cancel"
        onConfirm={handleActivate}
      />

      {showCreditModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Credit Restaurant Wallet
              </h3>
              <button
                onClick={closeCreditModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Enter the amount to credit to <strong>{restaurant.name}</strong>'s
              wallet.
            </p>
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Amount (₦)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={creditAmount}
                onChange={(event) => setCreditAmount(event.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Enter amount"
              />
              {creditError && (
                <div className="text-sm text-red-600">{creditError}</div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeCreditModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreditRestaurant}
                disabled={isCrediting}
                className={`px-4 py-2 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 transition-colors ${
                  isCrediting ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {isCrediting ? "Crediting..." : "Credit Wallet"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        title="Success"
        message={successMessage}
        confirmText="OK"
        onConfirm={() => setIsSuccessModalOpen(false)}
      />

      <Modal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="Error"
        message={errorMessage || "Something went wrong."}
        confirmText="OK"
        onConfirm={() => setIsErrorModalOpen(false)}
      />

      {showSuspensionModal && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full relative">
            <button
              onClick={() => setShowSuspensionModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
            >
              <FiX className="w-5 h-5 text-gray-500" />
            </button>
            <CreateSuspension
              suspendeeId={restaurant._id}
              type="restaurant"
              onClose={() => setShowSuspensionModal(false)}
            />
          </div>
        </div>
      )}

      <ChargeRateModal
        isOpen={showChargeRateModal}
        onClose={() => setShowChargeRateModal(false)}
        onSubmit={handleUpdateChargeRate}
      />
    </div>
  );
};

// Helper component for detail cards
const DetailCard = ({
  icon,
  title,
  value,
}: {
  icon: React.ReactNode;
  title: string;
  value: React.ReactNode;
}) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="flex items-center gap-3 mb-2">
      <div className="text-gray-500">{icon}</div>
      <h3 className="font-medium text-gray-700">{title}</h3>
    </div>
    <div className="text-gray-900 pl-8">{value}</div>
  </div>
);

export default RestaurantDetails;
